import React, { useState, useEffect, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
	Dialog,
	DialogContent,
	DialogTitle,
	Button,
	DialogActions,
	CircularProgress,
	TextField,
	Switch,
	FormControlLabel,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Chip,
	Paper,
	Typography,
	IconButton,
} from "@material-ui/core";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import MessageBubblePreview from "../MessageBubblePreview";
import { AuthContext } from "../../context/Auth/AuthContext";
import { hasMenuPermission } from "../../layout/MainListItems";
import { getBackendUrl } from "../../config";
import {
	buildMessageVariables,
	fetchWasheetMessageVariables,
} from "../../utils/messageVariables";

const SERVER_TIME_ZONE = "__server_time_zone__";

const useStyles = makeStyles(theme => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},

	multFieldLine: {
		display: "flex",
		"& > *:not(:last-child)": {
			marginRight: theme.spacing(1),
		},
	},

	btnWrapper: {
		position: "relative",
	},

	buttonProgress: {
		color: green[500],
		position: "absolute",
		top: "50%",
		left: "50%",
		marginTop: -12,
		marginLeft: -12,
	},
	sectionTitle: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(1),
		fontWeight: 600,
	},
	variableBox: {
		display: "flex",
		flexWrap: "wrap",
		gap: theme.spacing(0.5),
		padding: theme.spacing(1),
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	snippetBox: {
		padding: theme.spacing(1.5),
		marginTop: theme.spacing(1),
		backgroundColor: theme.palette.background.default,
		overflowX: "auto",
	},
	snippetCode: {
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
		fontSize: 12,
	},
	messagePreviewGrid: {
		display: "grid",
		gridTemplateColumns: "330px minmax(0, 1fr)",
		gap: theme.spacing(1),
		alignItems: "start",
		marginTop: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			gridTemplateColumns: "290px minmax(0, 1fr)",
		},
		[theme.breakpoints.down("xs")]: {
			gridTemplateColumns: "1fr",
		},
	},
	inlineActions: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
	},
}));

const SessionSchema = Yup.object().shape({
	name: Yup.string()
		.min(2, "Muy corto")
		.max(50, "Muy largo")
		.required("Requerido"),
});

const getBrowserTimeZone = () =>
	Intl.DateTimeFormat().resolvedOptions().timeZone || "";

const parseWidgetDomains = value => {
	if (!value) return [];
	if (Array.isArray(value)) return value;

	try {
		const parsed = JSON.parse(value);
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return String(value)
			.split(/[\n,]/)
			.map(domain => domain.trim())
			.filter(Boolean);
	}
};

const stringifyWidgetDomains = domains =>
	JSON.stringify(
		String(domains || "")
			.split(/[\n,]/)
			.map(domain => domain.trim())
			.filter(Boolean)
	);

const isMetaChannel = channel => ["messenger", "instagram"].includes(channel);

const getAssignmentMode = mode =>
	mode === "normal" ? "normal" : "multiagent";

const parseMetaConfig = session => {
	if (!session) return {};
	try {
		const parsed = JSON.parse(session);
		return parsed && typeof parsed === "object" ? parsed : {};
	} catch {
		return {};
	}
};

const timeZoneOptions =
	typeof Intl.supportedValuesOf === "function"
		? Intl.supportedValuesOf("timeZone")
		: [
				"America/Costa_Rica",
				"America/Mexico_City",
				"America/Bogota",
				"America/Lima",
				"America/Panama",
				"America/Guatemala",
				"America/El_Salvador",
				"America/Tegucigalpa",
				"America/Managua",
				"America/Santo_Domingo",
				"America/Caracas",
				"America/Santiago",
				"America/Argentina/Buenos_Aires",
				"America/New_York",
				"America/Chicago",
				"America/Denver",
				"America/Los_Angeles",
				"UTC",
		  ];

const WhatsAppModal = ({ open, onClose, whatsAppId, initialChannel = "whatsapp" }) => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	const canUseTickets = hasMenuPermission(user, "tickets");
	const canUseMetaConnections = ["superadmin", "owner"].includes(user?.profile);
	const hideResellerControls = Boolean(user?.company?.resellerBranding);
	const assignmentModeStorageKey = `ticketAssignmentMode:${
		user?.companyId || user?.company?.id || "default"
	}`;
	const initialState = {
		name: "",
		greetingMessage: "",
		queueRedirectEnabled: false,
		farewellMessage: "",
		isDefault: false,
		timeZone: getBrowserTimeZone(),
		channel: initialChannel || "whatsapp",
		widgetAllowedDomains: "",
		widgetToken: "",
	};
	const [whatsApp, setWhatsApp] = useState(initialState);
	const [selectedQueueIds, setSelectedQueueIds] = useState([]);
	const [widgetDomains, setWidgetDomains] = useState("");
	const [activeTemplateField, setActiveTemplateField] =
		useState("greetingMessage");
	const [washeetVariables, setWasheetVariables] = useState([]);
	const [assignmentMode, setAssignmentMode] = useState(() => {
		const storedMode = localStorage.getItem(assignmentModeStorageKey);
		return getAssignmentMode(storedMode);
	});
	const templateVariables = buildMessageVariables([], washeetVariables);
	const isNormalMode = assignmentMode === "normal";
	const shouldShowMessengerOption =
		canUseMetaConnections || whatsApp.channel === "messenger";
	const shouldShowInstagramOption =
		canUseMetaConnections || whatsApp.channel === "instagram";

	useEffect(() => {
		const storedMode = localStorage.getItem(assignmentModeStorageKey);
		if (storedMode) {
			setAssignmentMode(getAssignmentMode(storedMode));
		}

		const handleAssignmentModeChanged = event => {
			setAssignmentMode(getAssignmentMode(event?.detail?.mode));
		};

		window.addEventListener(
			"ticketAssignmentModeChanged",
			handleAssignmentModeChanged
		);

		return () => {
			window.removeEventListener(
				"ticketAssignmentModeChanged",
				handleAssignmentModeChanged
			);
		};
	}, [assignmentModeStorageKey]);

	useEffect(() => {
		if (!open) return;

		fetchWasheetMessageVariables()
			.then(setWasheetVariables)
			.catch(() => undefined);
	}, [open]);

	useEffect(() => {
		const fetchSession = async () => {
			if (!whatsAppId) return;

			try {
				const { data } = await api.get(`/connections/${whatsAppId}`);
				setWhatsApp({ ...data, ...parseMetaConfig(data.session) });
				setWidgetDomains(parseWidgetDomains(data.widgetAllowedDomains).join("\n"));

				const whatsQueueIds = data.queues?.map(queue => queue.id);
				setSelectedQueueIds(whatsQueueIds);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, [whatsAppId]);

	useEffect(() => {
		if (!open || whatsAppId) return;
		setWhatsApp({
			...initialState,
			channel: initialChannel || "whatsapp",
			timeZone: getBrowserTimeZone(),
		});
		setWidgetDomains("");
		setSelectedQueueIds([]);
	}, [open, whatsAppId, initialChannel]);

	const handleSaveWhatsApp = async values => {
		const channel = values.channel || "whatsapp";
		if (isMetaChannel(channel) && !canUseMetaConnections) {
			toast.error("Canal disponible solo para superadmin o dueño");
			return;
		}
		const connectionData = {
			...values,
			channel,
			status:
				channel === "webchat" || isMetaChannel(channel)
					? values.status || "CONNECTED"
					: values.status,
			queueRedirectEnabled: Boolean(values.queueRedirectEnabled),
			queueIds: selectedQueueIds,
			widgetAllowedDomains:
				channel === "webchat" ? stringifyWidgetDomains(widgetDomains) : null,
		};

		try {
			if (whatsAppId) {
				await api.put(`/connections/${whatsAppId}`, connectionData);
			} else {
				await api.post("/connections", connectionData);
			}
			toast.success(i18n.t("connectionModal.success"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	const handleClose = () => {
		onClose();
		setWhatsApp({
			...initialState,
			channel: initialChannel || "whatsapp",
			timeZone: getBrowserTimeZone(),
		});
		setWidgetDomains("");
	};

	const handleCopy = async text => {
		try {
			await navigator.clipboard.writeText(text);
			toast.success("Copiado");
		} catch (err) {
			toastError(err);
		}
	};

	const handleRegenerateWidgetToken = async () => {
		try {
			const { data } = await api.post(`/connections/${whatsAppId}/widget-token`);
			setWhatsApp(data);
			toast.success("Token actualizado");
		} catch (err) {
			toastError(err);
		}
	};

	const handleInsertTemplateVariable = (values, setFieldValue, variable) => {
		const fieldName = activeTemplateField || "greetingMessage";
		const currentValue = values[fieldName] || "";
		const separator = currentValue && !/\s$/.test(currentValue) ? " " : "";
		setFieldValue(fieldName, `${currentValue}${separator}${variable}`);
	};

	const getWidgetSnippet = values => {
		const backendUrl = (getBackendUrl() || window.location.origin).replace(/\/+$/, "");
		const initialMessage =
			values.greetingMessage || "Hola, ¿cómo podemos ayudarte?";

		return `<!-- Coloca este codigo dentro de la etiqueta <head>: -->
<script>
  window.KODELR_WEBCHAT = {
    config: {
      token: "${values.widgetToken || ""}",
      position: "right",
      initialMessages: [${JSON.stringify(initialMessage.replace(/\n/g, " "))}],
      toggleButtonEnabled: true,
      toggleButtonBgColor: "#2576d2",
      isAudioEnabled: false
    }
  };
</script>
<script async crossorigin="*" src="${backendUrl}/webchat/widget.js"></script>`;
	};

	return (
		<div className={classes.root}>
				<Dialog
					open={open}
					onClose={handleClose}
				maxWidth="md"
				fullWidth
				scroll="paper"
			>
				<DialogTitle>
					<span className={classes.inlineActions}>
						<span>
							{whatsAppId
								? i18n.t("connectionModal.title.edit")
								: i18n.t("connectionModal.title.add")}
						</span>
						{whatsAppId && whatsApp.channel === "webchat" && (
							<IconButton
								size="small"
								aria-label="Copiar identificación"
								onClick={() => handleCopy(whatsApp.widgetToken || "")}
							>
								<FileCopyOutlinedIcon fontSize="small" />
							</IconButton>
						)}
					</span>
				</DialogTitle>
				<Formik
					initialValues={whatsApp}
					enableReinitialize={true}
					validationSchema={SessionSchema}
					onSubmit={(values, actions) => {
						setTimeout(() => {
							handleSaveWhatsApp(values);
							actions.setSubmitting(false);
						}, 400);
					}}
				>
					{({ values, touched, errors, isSubmitting, setFieldValue }) => (
						<Form>
							<DialogContent dividers>
								<div className={classes.multFieldLine}>
									<Field
										as={TextField}
										label={i18n.t("connectionModal.form.name")}
										autoFocus
										name="name"
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										margin="dense"
										className={classes.textField}
									/>
									{!hideResellerControls && (
										<FormControlLabel
											control={
												<Field
													as={Switch}
													color="primary"
													name="isDefault"
													checked={values.isDefault}
												/>
											}
											label={i18n.t("connectionModal.form.default")}
										/>
									)}
								</div>
								<FormControl variant="outlined" margin="dense" fullWidth>
									<InputLabel>Tipo de conexión</InputLabel>
									<Field
										as={Select}
										name="channel"
										label="Tipo de conexión"
										disabled={Boolean(whatsAppId)}
									>
										<MenuItem value="whatsapp">WhatsApp</MenuItem>
										<MenuItem value="webchat">Web Chat</MenuItem>
										{shouldShowMessengerOption && (
											<MenuItem value="messenger">Messenger</MenuItem>
										)}
										{shouldShowInstagramOption && (
											<MenuItem value="instagram">Instagram</MenuItem>
										)}
									</Field>
								</FormControl>
								{values.channel !== "webchat" && !isMetaChannel(values.channel) && (
									<FormControl variant="outlined" margin="dense" fullWidth>
										<InputLabel>
											{i18n.t("connectionModal.form.timeZone")}
										</InputLabel>
										<Field
											as={Select}
											name="timeZone"
											value={values.timeZone || SERVER_TIME_ZONE}
											label={i18n.t("connectionModal.form.timeZone")}
											onChange={event =>
												setFieldValue(
													"timeZone",
													event.target.value === SERVER_TIME_ZONE
														? ""
														: event.target.value
												)
											}
										>
											<MenuItem value={SERVER_TIME_ZONE}>
												{i18n.t("connectionModal.form.serverTimeZone")}
											</MenuItem>
											{timeZoneOptions.map(timeZone => (
												<MenuItem key={timeZone} value={timeZone}>
													{timeZone}
												</MenuItem>
											))}
										</Field>
									</FormControl>
								)}
								{isMetaChannel(values.channel) && (
									<>
										<Typography className={classes.sectionTitle}>
											Conexion Meta
										</Typography>
										<Paper variant="outlined" className={classes.snippetBox}>
											<Typography variant="body2">
												La autenticacion y los tokens se gestionan desde el flujo de conexion con Meta. Para reautorizar, vuelve a usar Agregar conexion y selecciona el canal correspondiente.
											</Typography>
											<Typography variant="body2">
												Webhook Messenger: {(getBackendUrl() || window.location.origin).replace(/\/+$/, "")}/meta/webhooks/messenger
											</Typography>
											<Typography variant="body2">
												Webhook Instagram: {(getBackendUrl() || window.location.origin).replace(/\/+$/, "")}/meta/webhooks/instagram
											</Typography>
										</Paper>
									</>
								)}
								{canUseTickets && !isNormalMode && (
									<>
										<div className={classes.messagePreviewGrid}>
											<MessageBubblePreview block={{ text: values.greetingMessage }} />
											<Field
												as={TextField}
												label={i18n.t("queueModal.form.greetingMessage")}
												type="greetingMessage"
												multiline
												rows={5}
												fullWidth
												name="greetingMessage"
												onFocus={() => setActiveTemplateField("greetingMessage")}
												error={
													touched.greetingMessage && Boolean(errors.greetingMessage)
												}
												helperText={
													touched.greetingMessage && errors.greetingMessage
												}
												variant="outlined"
												margin="dense"
											/>
										</div>
										<FormControlLabel
											control={
												<Field
													as={Switch}
													color="primary"
													name="queueRedirectEnabled"
													checked={values.queueRedirectEnabled}
												/>
											}
											label={i18n.t("connectionModal.form.queueRedirectEnabled")}
										/>
										<div className={classes.messagePreviewGrid}>
											<MessageBubblePreview block={{ text: values.farewellMessage }} />
											<Field
												as={TextField}
												label={i18n.t("connectionModal.form.farewellMessage")}
												type="farewellMessage"
												multiline
												rows={5}
												fullWidth
												name="farewellMessage"
												onFocus={() => setActiveTemplateField("farewellMessage")}
												error={
													touched.farewellMessage && Boolean(errors.farewellMessage)
												}
												helperText={
													touched.farewellMessage && errors.farewellMessage
												}
												variant="outlined"
												margin="dense"
											/>
										</div>
										<QueueSelect
											selectedQueueIds={selectedQueueIds}
											onChange={selectedIds => setSelectedQueueIds(selectedIds)}
										/>
										<Paper variant="outlined" className={classes.variableBox}>
											{templateVariables.map(variable => (
												<Chip
													key={variable.value}
													size="small"
													label={variable.label}
													onClick={() =>
														handleInsertTemplateVariable(
															values,
															setFieldValue,
															variable.value
														)
													}
												/>
											))}
										</Paper>
									</>
								)}
								{values.channel === "webchat" && (
									<>
										<Typography className={classes.sectionTitle}>
											Widget Integration
										</Typography>
										<TextField
											label="Dominios permitidos"
											placeholder="https://mi-sitio-web.com"
											helperText="Escribe un dominio por línea o sepáralos por comas."
											value={widgetDomains}
											onChange={event => setWidgetDomains(event.target.value)}
											variant="outlined"
											margin="dense"
											fullWidth
											multiline
											rows={3}
										/>
										{values.widgetToken && (
											<>
												<Typography
													variant="subtitle2"
													className={classes.inlineActions}
												>
													<IconButton
														size="small"
														aria-label="Copiar"
														onClick={() => handleCopy(getWidgetSnippet(values))}
													>
														<FileCopyOutlinedIcon fontSize="small" />
													</IconButton>
													Code snippet:
												</Typography>
												<Paper variant="outlined" className={classes.snippetBox}>
													<code className={classes.snippetCode}>
														{getWidgetSnippet(values)}
													</code>
												</Paper>
												<Button
													color="primary"
													startIcon={<RefreshIcon />}
													onClick={handleRegenerateWidgetToken}
												>
													Cambiar token
												</Button>
											</>
										)}
									</>
								)}
							</DialogContent>
							<DialogActions>
								<Button
									onClick={handleClose}
									color="secondary"
									disabled={isSubmitting}
									variant="outlined"
								>
									{i18n.t("connectionModal.buttons.cancel")}
								</Button>
								<Button
									type="submit"
									color="primary"
									disabled={isSubmitting}
									variant="contained"
									className={classes.btnWrapper}
								>
									{whatsAppId
										? i18n.t("connectionModal.buttons.okEdit")
										: i18n.t("connectionModal.buttons.okAdd")}
									{isSubmitting && (
										<CircularProgress
											size={24}
											className={classes.buttonProgress}
										/>
									)}
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</Dialog>
		</div>
	);
};

export default React.memo(WhatsAppModal);

